import { capitalize } from 'lodash'
import {
  BudgetPeriodState,
  BudgetRequest,
  Period,
  RequestPayload,
  TypeTeam,
} from 'src/pages/budget/budgetRequests/types'
import { STATUS, STATUS_PERIOD } from 'src/pages/budget/const'
import { TStatusPeriod } from 'src/pages/budget/types'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'

dayjs.locale('ru')

export const filterOption = (
  inputValue: string,
  option?: { label?: string },
) => {
  if (!option?.label) return false
  return (
    option.label.toLowerCase().includes(inputValue.trim().toLowerCase()) ??
    false
  )
}

export const getOptionsForArticles = (list: BudgetRequest[]) => {
  const map = new Map<number, string>()

  for (const { article } of list) {
    if (!map.has(article.id)) {
      map.set(article.id, article.name)
    }
  }

  return Array.from(map, ([value, label]) => ({ value, label }))
}

export const getOptionsForCreatedBy = (list: BudgetRequest[]) => {
  const map = new Map<string, string>()

  for (const { createdBy } of list) {
    if (!map.has(createdBy.id)) {
      map.set(createdBy.id, createdBy.fullname)
    }
  }

  return Array.from(map, ([value, label]) => ({ value, label }))
}

export const getDefaultTeam = (options: TypeTeam[]): string | null => {
  if (options.length !== 1) return null

  return String(options[0].teamId)
}

export const sortBudgetRequests = (items: BudgetRequest[]): BudgetRequest[] => {
  return [...items].sort((a, b) => {
    if (a.status === STATUS.DRAFT && b.status !== STATUS.DRAFT) return -1
    if (a.status !== STATUS.DRAFT && b.status === STATUS.DRAFT) return 1

    return dayjs(b.updatedAt).diff(dayjs(a.updatedAt))
  })
}

export const filterBudgetList = (
  list: BudgetRequest[],
  filters: Record<string, any>,
) => {

  if (!Object.keys(filters).length) return list
  const searchText = filters.searchText?.trim().toLowerCase()

  return list.filter((item) => {
    if (filters.articleName?.length) {
      if (!filters.articleName.includes(item.article.id)) return false
    }

    if (filters.createdBy?.length) {
      if (!filters.createdBy.includes(item.createdBy.id)) return false
    }

    if (filters.status?.length) {
      if (!filters.status.includes(item.status)) return false
    }

    if (filters.products?.length) {
      if (!item.productIds.some((p) => filters.products.includes(p)))
        return false
    }

    if (searchText) {
      const description = item.description?.toLowerCase() ?? ''
      if (!description.includes(searchText)) return false
    }

    return true
  })
}

export function formatPeople(count: number) {
  const pluralRules = new Intl.PluralRules('ru-RU', { type: 'cardinal' })
  const forms = {
    one: 'человек',
    few: 'человека',
    many: 'человек',
    other: 'человек',
  }
  const rule = pluralRules.select(count) as keyof typeof forms
  return `${count} ${forms[rule]}`
}

export function formatRequests(count: number) {
  const pluralRules = new Intl.PluralRules('ru-RU', { type: 'cardinal' })
  const forms = {
    one: 'заявку',
    few: 'заявки',
    many: 'заявок',
    other: 'заявки',
  }
  const rule = pluralRules.select(count) as keyof typeof forms
  return `${count} ${forms[rule]}`
}

export const getOptionsSegmentPeriod = (
  periods: Period[],
) => {
  const isSubmissionPeriod = (period: Period) =>
    period.status === STATUS_PERIOD.OPEN_CREATE ||
    period.status === STATUS_PERIOD.CLOSE_CREATE
  const sortedPeriods = [...periods].sort(
    (a, b) =>
      dayjs(a.periodFormatted, 'YYYY-MM').valueOf() -
      dayjs(b.periodFormatted, 'YYYY-MM').valueOf(),
  )
  const paymentPeriod = sortedPeriods.find(
    (period) => period.status === 'open_payment',
  )

  let quickPeriods: Period[]

  if (paymentPeriod) {
    const paymentIndex = sortedPeriods.findIndex(
      (period) => period.id === paymentPeriod.id,
    )
    const periodsAfterPayment = sortedPeriods
      .slice(paymentIndex + 1)
      .filter(isSubmissionPeriod)

    quickPeriods =
      periodsAfterPayment.length >= 2
        ? [paymentPeriod, ...periodsAfterPayment.slice(0, 2)]
        : [
            ...sortedPeriods
              .slice(0, paymentIndex)
              .filter((period) => period.status === 'closed')
              .slice(-1),
            paymentPeriod,
            ...periodsAfterPayment.slice(0, 1),
          ]
  } else {
    quickPeriods = sortedPeriods
      .filter(isSubmissionPeriod)
      .slice(0, 3)
  }

  return quickPeriods.map((period) => ({
    label: capitalize(
      dayjs(period.periodFormatted, 'YYYY-MM').format('MMMM YYYY'),
    ),
    value: period.id,
    key: period.id,
  }))
}

const getLastPeriodByStatus = (
  periods: Period[],
  status: TStatusPeriod,
): Period | undefined => {
  return periods
    .filter((item) => item.status === status)
    .sort(
      (a, b) =>
        dayjs(b.periodFormatted, 'YYYY-MM').valueOf() -
        dayjs(a.periodFormatted, 'YYYY-MM').valueOf(),
    )[0]
}

export const getBudgetPeriodState = (periods: Period[]): BudgetPeriodState => {
  const byPeriod = new Map<string, number>()
  const byId = new Map<number, Period>()

  for (const item of periods) {
    byPeriod.set(item.periodFormatted, item.id)
    byId.set(item.id, item)
  }
  const activePeriod =
    [...periods]
      .filter((item) => item.status === 'open_create')
      .sort(
        (a, b) =>
          dayjs(a.periodFormatted, 'YYYY-MM').valueOf() -
          dayjs(b.periodFormatted, 'YYYY-MM').valueOf(),
      )[0] ??
    getLastPeriodByStatus(periods, 'open_payment') ??
    getLastPeriodByStatus(periods, 'closed') ??
    periods[0]

  return {
    byPeriod,
    byId,
    currentPeriodId: activePeriod?.id ?? null,
    segmentedPeriodOptions: getOptionsSegmentPeriod(periods),
  }
}

const isEqualArray = <T>(a: T[] = [], b: T[] = []) =>
  a.length === b.length && a.every((item) => b.includes(item))

export const isEqualObject = (
  body: Partial<RequestPayload>,
  initialValues: Partial<RequestPayload>,
) => {
  return (Object.keys(body) as Array<keyof RequestPayload>).every((key) => {
    if (key === 'productIds') {
      return isEqualArray(body.productIds ?? [], initialValues.productIds ?? [])
    }

    return body[key] === initialValues[key]
  })
}
